import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useIsPhone from '../utils/useIsPhone';

const NAVY      = '#0D1B2A';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

// One centered container for every Trades Resource Directory tab (/resources and
// /union-wages), so the heading, tab row and tab content share the same left and right edges.
export const DIRECTORY_CONTAINER = { width: '100%', maxWidth: '1000px', margin: '0 auto', padding: '28px 20px 60px' };

// Heading block shown above the tab row on every directory tab.
export function DirectoryHeading() {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  return (
    <>
      <p style={{ fontSize: isPhone ? '0.8rem' : '0.7rem', fontWeight: 700, color: AMBER, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
        {t('resources.publicPage.eyebrow')}
      </p>
      <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.1rem', color: WHITE, letterSpacing: '0.03em', marginBottom: '10px' }}>
        {t('resources.publicPage.title')}
      </h1>
      <p style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, marginBottom: '24px', maxWidth: '640px' }}>
        {t('resources.publicPage.subtitle')}
      </p>
    </>
  );
}

// Tab keys PublicResources renders in place; any of them can be deep-linked as /resources?tab=<key>.
export const RESOURCE_TAB_KEYS = ['school', 'union', 'licensing', 'pay'];

// The Trades Resource Directory tab row, shared by /resources and /union-wages. Union Wages is
// its own page, so that tab always navigates. On /resources (onSelect given) the other tabs
// switch in place; anywhere else they navigate back to /resources with the tab preselected.
// fullWidth stretches the row to its container so both edges line up with the content below.
export default function ResourceTabs({ activeTab, onSelect, fullWidth = false }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const tabs = [
    { key: 'school',      label: t('resources.tabs.schools') },
    { key: 'union',       label: t('resources.tabs.unions') },
    { key: 'licensing',   label: t('nav.worker.licensingCerts') },
    { key: 'pay',         label: t('nav.worker.careerPayGuide') },
    { key: 'unionWages',  label: t('resources.tabs.unionWages') },
  ];
  const select = key => {
    if (key === 'unionWages') navigate('/union-wages');
    else if (onSelect) onSelect(key);
    else navigate(`/resources?tab=${key}`);
  };

  // Phones: every tab visible at once as a two-column grid of 44px targets (the single scrolling
  // row hid Career Pay Guide and Union Wages off-screen); the last odd tab spans both columns.
  const rowStyle = isPhone
    ? { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '4px', width: '100%' }
    : { display: 'flex', gap: '4px', width: fullWidth ? '100%' : 'fit-content', maxWidth: '100%', overflowX: 'auto' };
  return (
    <div style={{
      ...rowStyle, marginBottom: '24px',
      backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '4px',
    }}>
      {tabs.map((tab, i) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => select(tab.key)}
          aria-current={activeTab === tab.key ? 'page' : undefined}
          style={{
            background: activeTab === tab.key ? AMBER : 'transparent',
            color: activeTab === tab.key ? NAVY : WHITE,
            border: 'none', borderRadius: '7px', padding: '9px 16px',
            fontSize: '0.82rem', fontWeight: 700, fontFamily: FONT_BODY,
            cursor: 'pointer', whiteSpace: 'nowrap', flex: fullWidth ? '1 0 auto' : undefined,
            ...(isPhone ? {
              minHeight: '44px', padding: '8px 10px', fontSize: '0.875rem', whiteSpace: 'normal', lineHeight: 1.2,
              gridColumn: i === tabs.length - 1 && tabs.length % 2 === 1 ? '1 / -1' : undefined,
            } : {}),
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
